
import type React from "react"
import useWindowSize from "./utils/useWindowSize"

interface BingoConfigItem {
  color: string
  startNum: number
  endNum: number
}

interface BingoConfig {
  [key: string]: BingoConfigItem
}

interface BingoCardProps {
  maxNumberRange?: number
  calledNumbers: (number | string)[]
  isMobile?: boolean
  className?: string
}

const Card: React.FC<BingoCardProps> = ({ maxNumberRange, calledNumbers = [], isMobile, className }) => {
  const { width } = useWindowSize()

  if (!maxNumberRange) return null
  const rows = 5

  const rangeStep = Math.floor(maxNumberRange / 5)
  const cols = rangeStep + 1
  const bingoConfig: BingoConfig = {
    B: { color: "bg-[#C5007D]", startNum: 0, endNum: rangeStep - 1 },
    I: { color: "bg-[#8BAF00]", startNum: rangeStep, endNum: rangeStep * 2 - 1 },
    N: { color: "bg-[#0282AA]", startNum: rangeStep * 2, endNum: rangeStep * 3 - 1 },
    G: { color: "bg-[#C04E03]", startNum: rangeStep * 3, endNum: rangeStep * 4 - 1 },
    O: { color: "bg-[#AB19CA]", startNum: rangeStep * 4, endNum: maxNumberRange - 1 },
  }

  const bingoLetters = Object.keys(bingoConfig)

  const getNumberBackgroundColor = (number: number) => {
    return number % 2 === 0 ? "bg-[#1C06B4]" : "bg-[#3F8DDE]"
  }

  const getCellSize = () => {
    if (width <= 320) {
      return maxNumberRange <= 50 ? "w-[20px] h-[20px]" : "w-[12px] h-[12px]"
    }
    else if (width <= 375) {
      return maxNumberRange <= 50 ? "w-[28px] h-[28px]" : "w-[14px] h-[14px]"
    } else if (width <= 425) {
      return maxNumberRange <= 50 ? "w-[28px] h-[28px]" : "w-[18px] h-[18px]"
    }
    else if (width <= 768) {
      return maxNumberRange <= 50 ? "w-[32px] h-[32px]" : "w-[30px] h-[30px]"
    }
    else if (width <= 1024) {
      return maxNumberRange <= 50 ? "w-[36px] h-[36px]" : "w-[30px] h-[30px]"
    }
    else if (width <= 1200) {
      return "w-[32px] h-[32px]"
    }
    else if (width <= 1440) {
      return maxNumberRange <= 50 ? "w-[36px] h-[36px]" : "w-[30px] h-[30px]"
    }
    else if (width <= 1600) {
      return maxNumberRange <= 50 ? "w-[36px] h-[36px]" : "w-[36px] h-[36px]"
    }
    else {
      return "w-[40px] h-[40px]"
    }
  }

  const getFontSize = () => {
    if (width <= 320) {
      return maxNumberRange <= 50 ? "text-[11px]" : "text-[9px]"
    } else if (width <= 375) {
      return maxNumberRange <= 50 ? "text-[13px]" : "text-[10px]"
    }
    else if (width <= 425) {
      return maxNumberRange <= 50 ? "text-[13px]" : "text-[9px]"
    } else if (width <= 768) {
      return maxNumberRange <= 50 ? "text-[16px]" : "text-[14px]"
    } else if (width <= 1024) {
      return maxNumberRange <= 50 ? "text-[18px]" : "text-[12px]"
    } else if (width <= 1440) {
      return maxNumberRange <= 50 ? "text-[18px]" : "text-[18px]"
    } else {
      return "text-[20px]"
    }
  }

  const getCalledCircleSize = () => {
    if (width <= 320) {
      return maxNumberRange <= 50 ? "w-5 h-5" : "w-5 h-5"
    } else if (width <= 375) {
      return maxNumberRange <= 50 ? "w-7 h-7" : "w-4 h-4"
    } else if (width <= 425) {
      return maxNumberRange <= 50 ? "w-7 h-7" : "w-4 h-4"
    } else if (width <= 768) {
      return maxNumberRange <= 50 ? "w-8 h-8" : "w-8 h-8"
    } else if (width <= 1440) {
      return maxNumberRange <= 50 ? "w-8 h-8" : "w-8 h-8"
    } else if (width <= 1600) {
      return maxNumberRange <= 50 ? "w-8 h-8" : "w-8 h-8"
    } else {
      return "w-10 h-10"
    }
  }

  const renderCell = (row: number, col: number) => {
    const actualRow = row
    const actualCol = col
    const letterConfig = bingoConfig[bingoLetters[actualRow]]
    const isLetterCell = actualCol === 0
    const number = isLetterCell ? 0 : letterConfig.startNum + actualCol
    const content = isLetterCell ? bingoLetters[actualRow] : number.toString()


    const isCalled =
      !isLetterCell &&
      (calledNumbers.includes(number) || calledNumbers.includes(String(number)));



    const cellBackgroundColor = isLetterCell ? letterConfig.color : getNumberBackgroundColor(number)

    const isEven = number % 2 === 0
    const borderColorClass = isEven ? "border-[#3F8DDE]" : "border-[#1C06B4]"

    const cellSize = getCellSize()
    const fontSize = getFontSize()
    const calledCircleSize = getCalledCircleSize()

    return (
      <div key={`${row}-${col}`} className={`relative ${cellSize} flex items-center justify-center`}>
        <div
          className={`${cellBackgroundColor} w-full h-full flex items-center justify-center text-white font-bold ${isCalled ? "relative" : ""}`}
        >
          {isCalled ? (
            <>
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className={`${calledCircleSize} rounded-full bg-yellow-300 ${borderColorClass} border-4 gap-1 flex items-center justify-center`}
                >
                  <span className={`${fontSize} text-black`}>{content}</span>
                </div>
              </div>
            </>
          ) : (
            <span className={fontSize}>{content}</span>
          )}
        </div>
      </div>
    )
  }

  const gridRows = rows
  const gridCols = cols

  const getPadding = () => {
    if (width <= 320) {
      return "p-1"
    } else if (width <= 768) {
      return "p-1.5"
    } else {
      return "p-2"
    }
  }

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className={`bg-[#00BBE7] w-full h-full ${getPadding()} rounded-md`}>
        <div
          className="grid gap-0.5"
          style={{
            gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${gridRows}, minmax(0, 1fr))`,
          }}
        >
          {Array.from({ length: rows }, (_, row) => Array.from({ length: cols }, (_, col) => renderCell(row, col)))}
        </div>
      </div>
    </div>
  )
}

export default Card

